import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  acceptPastPaper,
  acceptQuestion,
  addManualQuestion,
  addPastPaper,
  AssessmentQuestionDTO,
  deletePastPaper,
  deleteQuestion,
  generatePastPapers,
  generateQuestions,
  getNodeAssessmentConfig,
  getPastPapers,
  getPendingQuestions,
  getPublishedQuestions,
  getSubmissionDetail,
  listSubmissions,
  PastPaperDTO,
  submitEvaluation,
  SubmissionDetailDTO,
  SubmissionSummaryDTO,
} from '../services/tutorAssessmentService';

export default function TutorTopicAssessmentPage() {
  const { courseId, nodeId } = useParams<{ courseId: string; nodeId: string }>();
  const navigate = useNavigate();
  const cId = Number(courseId);
  const nId = Number(nodeId);

  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link underline="hover" sx={{ cursor: 'pointer' }} onClick={() => navigate('/tutor/courses')}>
          My Courses
        </Link>
        <Link underline="hover" sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/tutor/courses/${cId}/structure`)}>
          Course Structure
        </Link>
        <Typography color="text.primary">Topic Assessment</Typography>
      </Breadcrumbs>

      <Typography variant="h5" fontWeight={600} gutterBottom>Topic Assessment</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Questions" />
        <Tab label="Submissions" />
        <Tab label="Past Papers" />
      </Tabs>

      {tab === 0 && <QuestionsTab courseId={cId} nodeId={nId} />}
      {tab === 1 && <SubmissionsTab courseId={cId} nodeId={nId} />}
      {tab === 2 && <PastPapersTab courseId={cId} nodeId={nId} />}
    </Box>
  );
}

// ─── Questions Tab ─────────────────────────────────────────────────────────────

function QuestionsTab({ courseId, nodeId }: { courseId: number; nodeId: number }) {
  const [published, setPublished] = useState<AssessmentQuestionDTO[]>([]);
  const [pending, setPending] = useState<AssessmentQuestionDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newQ, setNewQ] = useState({ questionText: '', modelAnswer: '', allocatedMarks: 10 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pub, pend] = await Promise.all([
        getPublishedQuestions(courseId, nodeId),
        getPendingQuestions(courseId, nodeId),
      ]);
      setPublished(pub);
      setPending(pend);
    } catch {
      setError('Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [courseId, nodeId]);

  useEffect(() => { load(); }, [load]);

  async function handleAdd() {
    try {
      await addManualQuestion(courseId, nodeId, newQ);
      setAddDialogOpen(false);
      setNewQ({ questionText: '', modelAnswer: '', allocatedMarks: 10 });
      load();
    } catch { /* toast handled by api interceptor */ }
  }

  async function handleGenerate() {
    setGenerating(true);
    try {
      await generateQuestions(courseId, nodeId);
      load();
    } catch { /* handled */ } finally {
      setGenerating(false);
    }
  }

  async function handleAccept(qId: number) {
    try { await acceptQuestion(courseId, nodeId, qId); load(); } catch { /* handled */ }
  }

  async function handleDelete(qId: number) {
    try { await deleteQuestion(courseId, nodeId, qId); load(); } catch { /* handled */ }
  }

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Stack direction="row" spacing={1} mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
          Add Question
        </Button>
        <Button variant="outlined" startIcon={<AutoAwesomeIcon />}
          onClick={handleGenerate} disabled={generating}>
          {generating ? 'Generating…' : 'AI Generate'}
        </Button>
      </Stack>

      {pending.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Pending AI Review ({pending.length})
          </Typography>
          <Stack spacing={1}>
            {pending.map(q => (
              <Paper key={q.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={500}>{q.questionText}</Typography>
                    {q.modelAnswer && (
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        Model answer: {q.modelAnswer}
                      </Typography>
                    )}
                    <Typography variant="caption">{q.allocatedMarks} marks</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Accept">
                      <IconButton size="small" color="success" onClick={() => handleAccept(q.id)}>
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(q.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
          <Divider sx={{ my: 2 }} />
        </Box>
      )}

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Published Questions ({published.length})
      </Typography>
      {published.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No published questions yet. Add manually or use AI Generate.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {published.map((q, i) => (
            <Paper key={q.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={500}>
                    {i + 1}. {q.questionText}
                  </Typography>
                  {q.modelAnswer && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      Model answer: {q.modelAnswer}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} mt={0.5}>
                    <Chip label={`${q.allocatedMarks} marks`} size="small" />
                    <Chip label={q.source} size="small" variant="outlined" />
                  </Stack>
                </Box>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => handleDelete(q.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Assessment Question</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Question text" multiline rows={3} fullWidth
              value={newQ.questionText}
              onChange={e => setNewQ(p => ({ ...p, questionText: e.target.value }))} />
            <TextField label="Model answer" multiline rows={3} fullWidth
              value={newQ.modelAnswer}
              onChange={e => setNewQ(p => ({ ...p, modelAnswer: e.target.value }))} />
            <TextField label="Allocated marks" type="number" fullWidth
              value={newQ.allocatedMarks}
              onChange={e => setNewQ(p => ({ ...p, allocatedMarks: Number(e.target.value) }))}
              inputProps={{ min: 0 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}
            disabled={!newQ.questionText.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Submissions Tab ───────────────────────────────────────────────────────────

function SubmissionsTab({ courseId, nodeId }: { courseId: number; nodeId: number }) {
  const [submissions, setSubmissions] = useState<SubmissionSummaryDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SubmissionDetailDTO | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [evalDialogOpen, setEvalDialogOpen] = useState(false);
  const [mark, setMark] = useState('');
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    listSubmissions(courseId, nodeId)
      .then(setSubmissions)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [courseId, nodeId]);

  async function openDetail(submissionId: number) {
    setDetailLoading(true);
    try {
      const detail = await getSubmissionDetail(courseId, nodeId, submissionId);
      setSelected(detail);
      if (detail.evaluation) {
        setMark(String(detail.evaluation.mark));
        setFeedback(detail.evaluation.feedbackText || '');
      } else {
        setMark('');
        setFeedback('');
      }
    } catch { /* handled */ } finally {
      setDetailLoading(false);
    }
  }

  async function handleEvaluate() {
    if (!selected) return;
    setSubmitting(true);
    try {
      await submitEvaluation(courseId, nodeId, selected.submissionId, {
        mark: Number(mark),
        feedbackText: feedback || undefined,
      });
      setEvalDialogOpen(false);
      // Refresh the submission detail
      openDetail(selected.submissionId);
      // Refresh list
      listSubmissions(courseId, nodeId).then(setSubmissions).catch(() => {});
    } catch { /* handled */ } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <CircularProgress />;

  if (selected) {
    return (
      <Box>
        <Button onClick={() => setSelected(null)} sx={{ mb: 2 }}>← Back to submissions</Button>
        {detailLoading ? <CircularProgress /> : (
          <Box>
            <Typography variant="h6" gutterBottom>
              Submission from {selected.studentName}
            </Typography>
            <Chip label={selected.status} color={selected.status === 'EVALUATED' ? 'success' : 'warning'}
              sx={{ mb: 2 }} />

            <Typography variant="subtitle2" gutterBottom>Files</Typography>
            <Stack spacing={1} mb={2}>
              {selected.files.map(f => (
                <Paper key={f.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">{f.originalFilename}</Typography>
                    <Tooltip title="Open file">
                      <IconButton size="small" href={f.presignedUrl} target="_blank">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            <Typography variant="subtitle2" gutterBottom>Questions (with model answers)</Typography>
            <Stack spacing={1} mb={2}>
              {selected.questions.map((q, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2" fontWeight={500}>{i + 1}. {q.questionText}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Model: {q.modelAnswer} — {q.allocatedMarks} marks
                  </Typography>
                </Paper>
              ))}
            </Stack>

            {selected.evaluation && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Evaluated: {selected.evaluation.mark}/{selected.evaluation.totalMarks} marks
                {selected.evaluation.feedbackText && ` — ${selected.evaluation.feedbackText}`}
              </Alert>
            )}

            <Button variant="contained"
              onClick={() => setEvalDialogOpen(true)}>
              {selected.evaluation ? 'Update Evaluation' : 'Evaluate'}
            </Button>
          </Box>
        )}

        <Dialog open={evalDialogOpen} onClose={() => setEvalDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Evaluate Submission</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField label={`Mark (out of ${selected?.totalMarks ?? '?'})`}
                type="number" fullWidth value={mark}
                onChange={e => setMark(e.target.value)}
                inputProps={{ min: 0, max: selected?.totalMarks }} />
              <TextField label="Feedback (optional)" multiline rows={4} fullWidth
                value={feedback} onChange={e => setFeedback(e.target.value)} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEvalDialogOpen(false)} disabled={submitting}>Cancel</Button>
            <Button variant="contained" onClick={handleEvaluate}
              disabled={submitting || !mark}>
              {submitting ? 'Saving…' : 'Save Evaluation'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Student Submissions ({submissions.length})
      </Typography>
      {submissions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No submissions yet.</Typography>
      ) : (
        <Stack spacing={1}>
          {submissions.map(s => (
            <Paper key={s.submissionId} variant="outlined" sx={{ p: 2, cursor: 'pointer' }}
              onClick={() => openDetail(s.submissionId)}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" fontWeight={500}>{s.studentName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.fileCount} file{s.fileCount !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Chip label={s.status}
                  color={s.status === 'EVALUATED' ? 'success' : 'warning'} size="small" />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}

// ─── Past Papers Tab ───────────────────────────────────────────────────────────

function PastPapersTab({ courseId, nodeId }: { courseId: number; nodeId: number }) {
  const [papers, setPapers] = useState<PastPaperDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newP, setNewP] = useState({ questionText: '', modelAnswer: '', yearLabel: '', sourceExam: '', allocatedMarks: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { setPapers(await getPastPapers(courseId, nodeId)); }
    catch { /* handled */ } finally { setLoading(false); }
  }, [courseId, nodeId]);

  useEffect(() => { load(); }, [load]);

  async function handleAdd() {
    try {
      await addPastPaper(courseId, nodeId, {
        questionText: newP.questionText,
        modelAnswer: newP.modelAnswer || undefined,
        yearLabel: newP.yearLabel || undefined,
        sourceExam: newP.sourceExam || undefined,
        allocatedMarks: newP.allocatedMarks ? Number(newP.allocatedMarks) : undefined,
      });
      setAddDialogOpen(false);
      setNewP({ questionText: '', modelAnswer: '', yearLabel: '', sourceExam: '', allocatedMarks: '' });
      load();
    } catch { /* handled */ }
  }

  async function handleGenerate() {
    setGenerating(true);
    try { await generatePastPapers(courseId, nodeId); load(); }
    catch { /* handled */ } finally { setGenerating(false); }
  }

  async function handleAccept(id: number) {
    try { await acceptPastPaper(courseId, nodeId, id); load(); } catch { /* handled */ }
  }

  async function handleDelete(id: number) {
    try { await deletePastPaper(courseId, nodeId, id); load(); } catch { /* handled */ }
  }

  const pending = papers.filter(p => p.status === 'PENDING_REVIEW');
  const published = papers.filter(p => p.status === 'PUBLISHED');

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Stack direction="row" spacing={1} mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
          Add Past Paper
        </Button>
        <Button variant="outlined" startIcon={<AutoAwesomeIcon />}
          onClick={handleGenerate} disabled={generating}>
          {generating ? 'Generating…' : 'AI Suggest'}
        </Button>
      </Stack>

      {pending.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Pending Review ({pending.length})
          </Typography>
          <Stack spacing={1}>
            {pending.map(p => (
              <Paper key={p.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={500}>{p.questionText}</Typography>
                    {p.yearLabel && <Chip label={p.yearLabel} size="small" sx={{ mr: 0.5, mt: 0.5 }} />}
                    {p.sourceExam && <Chip label={p.sourceExam} size="small" variant="outlined" sx={{ mt: 0.5 }} />}
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Accept">
                      <IconButton size="small" color="success" onClick={() => handleAccept(p.id)}>
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
          <Divider sx={{ my: 2 }} />
        </Box>
      )}

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Published Past Papers ({published.length})
      </Typography>
      {published.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No past papers published yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {published.map((p, i) => (
            <Paper key={p.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={500}>{i + 1}. {p.questionText}</Typography>
                  {p.modelAnswer && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      Model answer: {p.modelAnswer}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={0.5} mt={0.5}>
                    {p.yearLabel && <Chip label={p.yearLabel} size="small" />}
                    {p.sourceExam && <Chip label={p.sourceExam} size="small" variant="outlined" />}
                    {p.allocatedMarks && <Chip label={`${p.allocatedMarks}m`} size="small" variant="outlined" />}
                    <Chip label={p.source} size="small" variant="outlined" />
                  </Stack>
                </Box>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Past Paper Question</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Question text" multiline rows={3} fullWidth
              value={newP.questionText}
              onChange={e => setNewP(p => ({ ...p, questionText: e.target.value }))} />
            <TextField label="Model answer (optional)" multiline rows={2} fullWidth
              value={newP.modelAnswer}
              onChange={e => setNewP(p => ({ ...p, modelAnswer: e.target.value }))} />
            <Stack direction="row" spacing={1}>
              <TextField label="Year (e.g. 2023)" fullWidth value={newP.yearLabel}
                onChange={e => setNewP(p => ({ ...p, yearLabel: e.target.value }))} />
              <TextField label="Source exam" fullWidth value={newP.sourceExam}
                onChange={e => setNewP(p => ({ ...p, sourceExam: e.target.value }))} />
            </Stack>
            <TextField label="Marks" type="number" fullWidth value={newP.allocatedMarks}
              onChange={e => setNewP(p => ({ ...p, allocatedMarks: e.target.value }))}
              inputProps={{ min: 0 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}
            disabled={!newP.questionText.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
